import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { retryNon4xx } from "shared/api/query-retry";
import { auctionsAdminApi } from "../../api/auctionsAdminApi";
var KEYS = {
    all: ["admin", "auctions"],
    byCollection: function (collectionId) {
        return ["admin", "auctions", "collection", collectionId];
    },
    one: function (id) { return ["admin", "auctions", id]; },
    bids: function (auctionId, filters) {
        return ["admin", "auctions", auctionId, "bids", filters !== null && filters !== void 0 ? filters : {}];
    },
};
export var useAdminAuctions = function () {
    return useQuery({
        queryKey: KEYS.all,
        queryFn: auctionsAdminApi.list,
        refetchInterval: 5000, // refresh status (scheduled→live→ended) periodically
        staleTime: 4000,
        retry: retryNon4xx,
    });
};
export var useAuctionsByCollection = function (collectionId) {
    return useQuery({
        queryKey: KEYS.byCollection(collectionId),
        queryFn: function () { return auctionsAdminApi.listByCollection(collectionId); },
        enabled: !!collectionId,
        staleTime: 4000,
        retry: retryNon4xx,
    });
};
export var useAdminAuction = function (id) {
    return useQuery({
        queryKey: KEYS.one(id),
        queryFn: function () { return auctionsAdminApi.get(id); },
        enabled: !!id,
        staleTime: 4000,
        retry: retryNon4xx,
    });
};
export var useAuctionBids = function (auctionId, filters) {
    return useQuery({
        queryKey: KEYS.bids(auctionId, filters),
        queryFn: function () { return auctionsAdminApi.listBids(auctionId, filters); },
        enabled: !!auctionId,
        staleTime: 4000,
        retry: retryNon4xx,
    });
};
export var useCreateAuction = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var collectionId = _a.collectionId, data = _a.data;
            return auctionsAdminApi.create(collectionId, data);
        },
        onSuccess: function (_data, _a) {
            var collectionId = _a.collectionId;
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.byCollection(collectionId) });
        },
    });
};
export var useUpdateAuction = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, data = _a.data;
            return auctionsAdminApi.update(id, data);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
            qc.invalidateQueries({ queryKey: KEYS.byCollection(data.collectionId) });
        },
    });
};
export var useCancelAuction = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return auctionsAdminApi.cancel(id); },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
            qc.invalidateQueries({ queryKey: KEYS.bids(data.id) });
        },
    });
};
export var useFinalizeAuction = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return auctionsAdminApi.finalize(id); },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.all });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
            qc.invalidateQueries({ queryKey: KEYS.bids(data.id) });
        },
    });
};
export var useDeleteAuction = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return auctionsAdminApi.remove(id); },
        onSuccess: function () { return qc.invalidateQueries({ queryKey: KEYS.all }); },
    });
};
export var useDeleteBid = function (auctionId) {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (bidId) { return auctionsAdminApi.deleteBid(bidId); },
        onSuccess: function () { return qc.invalidateQueries({ queryKey: ["admin", "auctions", auctionId, "bids"] }); },
    });
};
